import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { CalDateInfo } from '../../../../../types/schedule.types';
import type { DropdownMenuItem } from '../../../../../components/DropdownMenu/DropdownMenu';
import { useCalDateUpdates } from '../../../../../hooks/dataUpdates';

const CAL_DATE_SHIFT_KEYS = [
  'MORNING',
  'AFTERNOON',
  'EVENING',
  'APPENDIX1',
  'APPENDIX2',
  'APPENDIX3',
  'APPENDIX4',
  'APPENDIX5',
] as const;

const SHIFT_I18N_KEY: Record<(typeof CAL_DATE_SHIFT_KEYS)[number], string> = {
  MORNING: 'schedule.shiftMorning',
  AFTERNOON: 'schedule.shiftAfternoon',
  EVENING: 'schedule.shiftEvening',
  APPENDIX1: 'schedule.shiftAppendix1',
  APPENDIX2: 'schedule.shiftAppendix2',
  APPENDIX3: 'schedule.shiftAppendix3',
  APPENDIX4: 'schedule.shiftAppendix4',
  APPENDIX5: 'schedule.shiftAppendix5',
};

/** 计划日排班在界面上的展示：历史数据可能存英文枚举，展示为当前语言文案；否则原样显示用户自定义文本 */
export function formatCalDateShiftDisplay(raw: string | undefined, t: TFunction): string {
  if (!raw) return '';
  if (Object.prototype.hasOwnProperty.call(SHIFT_I18N_KEY, raw)) {
    return t(SHIFT_I18N_KEY[raw as keyof typeof SHIFT_I18N_KEY]);
  }
  return raw;
}

const formatDate = (year: number, month: number, day: number): string =>
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

export interface UseCalDateShiftEditParams {
  scheduleID: string | undefined;
  calDates: CalDateInfo[];
  onSlotCreate?: () => void;
  calendarScrollContainerRef?: React.RefObject<HTMLElement>; // 日历滚动容器引用，用于监听滚动更新下拉菜单位置
  /** 与点击日历格一致：选中该日 */
  onDateSelect?: (date: Date) => void;
}

export const useCalDateShiftEdit = ({
  scheduleID,
  calDates,
  onSlotCreate,
  calendarScrollContainerRef,
  onDateSelect,
}: UseCalDateShiftEditParams) => {
  const { t } = useTranslation();
  const { upsertCalDateData } = useCalDateUpdates();
  const [shiftDropdownVisible, setShiftDropdownVisible] = useState<string | null>(null);
  const [shiftDropdownPosition, setShiftDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const [shiftDropdownWidth, setShiftDropdownWidth] = useState<number | null>(null);
  const shiftInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [shiftEditingStates, setShiftEditingStates] = useState<{
    [key: string]: { value: string; isEditing: boolean };
  }>({});
  const shiftSelectInProgressRef = useRef(false);
  // 缓存下拉菜单的 DOM 引用，避免每次滚动都查询
  const shiftDropdownElementRef = useRef<HTMLElement | null>(null);
  // 缓存上一次的位置，避免不必要的更新
  const lastPositionRef = useRef<{ top: number; left: number } | null>(null);

  const getCalDateByDate = useCallback(
    (date: Date): CalDateInfo | undefined => {
      const dateStr = formatDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
      return calDates.find((pd) => {
        if (!pd.calDateDt) return false;
        const pdDateStr = pd.calDateDt.split('T')[0];
        return pdDateStr === dateStr;
      });
    },
    [calDates]
  );

  const notifyDateSelect = useCallback(
    (date: Date) => {
      onDateSelect?.(date);
    },
    [onDateSelect]
  );

  const handleShiftInputClick = useCallback((e: React.MouseEvent, date: Date) => {
    notifyDateSelect(date);
    e.stopPropagation();
    const dateKey = formatDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
    const inputElement = shiftInputRefs.current[dateKey];
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      setShiftDropdownWidth(Math.max(48, Math.round(rect.width)));
      setShiftDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
      setShiftDropdownVisible(dateKey);
    }
  }, [notifyDateSelect]);

  const handleShiftInputFocus = useCallback(
    (_e: React.FocusEvent<HTMLInputElement>, date: Date) => {
      notifyDateSelect(date);
      const dateKey = formatDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
      const calDate = getCalDateByDate(date);
      const raw = calDate?.calDateShift || '';
      const displayValue = formatCalDateShiftDisplay(raw || undefined, t);
      setShiftEditingStates((prev) => ({
        ...prev,
        [dateKey]: { value: displayValue, isEditing: true },
      }));
    },
    [notifyDateSelect, getCalDateByDate, t]
  );

  const handleShiftInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, date: Date) => {
    const dateKey = formatDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
    const newValue = e.target.value;
    setShiftEditingStates((prev) => ({
      ...prev,
      [dateKey]: { value: newValue, isEditing: true },
    }));
  }, []);

  const handleShiftInputBlur = useCallback(
    async (e: React.FocusEvent<HTMLInputElement>, date: Date) => {
      const dateKey = formatDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
      const editingState = shiftEditingStates[dateKey];
      if (!editingState) return;

      setTimeout(async () => {
        if (!scheduleID) return;
        if (shiftSelectInProgressRef.current) {
          shiftSelectInProgressRef.current = false;
          setShiftEditingStates((prev) => {
            const next = { ...prev };
            delete next[dateKey];
            return next;
          });
          return;
        }

        const dateStr = formatDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
        const trimmed = editingState.value.trim();
        const calDateShift = trimmed === '' ? undefined : trimmed;

        try {
          await upsertCalDateData(scheduleID, {
            calDateDt: dateStr,
            calDateBelongScheduleID: scheduleID,
            calDateShift,
          });
          if (onSlotCreate) {
            onSlotCreate();
          }
          setShiftEditingStates((prev) => {
            const next = { ...prev };
            delete next[dateKey];
            return next;
          });
        } catch (error) {
          console.error('更新计划日排班失败:', error);
          setShiftEditingStates((prev) => {
            const next = { ...prev };
            delete next[dateKey];
            return next;
          });
        }
      }, 200);
    },
    [scheduleID, shiftEditingStates, upsertCalDateData, onSlotCreate]
  );

  const handleShiftSelect = useCallback(
    async (date: Date, presetLabel: string) => {
      if (!scheduleID) return;
      shiftSelectInProgressRef.current = true;
      const dateStr = formatDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
      const dateKey = formatDate(date.getFullYear(), date.getMonth() + 1, date.getDate());

      try {
        await upsertCalDateData(scheduleID, {
          calDateDt: dateStr,
          calDateBelongScheduleID: scheduleID,
          calDateShift: presetLabel,
        });
        if (onSlotCreate) {
          onSlotCreate();
        }
        setShiftEditingStates((prev) => {
          const next = { ...prev };
          delete next[dateKey];
          return next;
        });
        setShiftDropdownVisible(null);
        const inputElement = shiftInputRefs.current[dateKey];
        if (inputElement) {
          inputElement.blur();
        }
      } catch (error) {
        console.error('更新计划日排班失败:', error);
        shiftSelectInProgressRef.current = false;
      }
    },
    [scheduleID, upsertCalDateData, onSlotCreate]
  );

  const shiftMenuItems: DropdownMenuItem[] = [
    { id: 'MORNING', label: t('schedule.shiftMorning') },
    { id: 'AFTERNOON', label: t('schedule.shiftAfternoon') },
    { id: 'EVENING', label: t('schedule.shiftEvening') },
  ];

  useEffect(() => {
    if (!shiftDropdownVisible) {
      setShiftDropdownWidth(null);
    }
  }, [shiftDropdownVisible]);

  // 监听日历滚动，直接更新下拉菜单 DOM 位置（0 延迟）
  useEffect(() => {
    if (!shiftDropdownVisible || !shiftDropdownPosition || !calendarScrollContainerRef?.current) {
      shiftDropdownElementRef.current = null;
      lastPositionRef.current = null;
      return;
    }
    
    const container = calendarScrollContainerRef.current;
    const dateKey = shiftDropdownVisible;
    
    // 初始化时查找并缓存下拉菜单的 DOM 元素
    const dropdownId = `shift-dropdown-${dateKey}`;
    shiftDropdownElementRef.current = document.querySelector(`[data-dropdown-id="${dropdownId}"]`) as HTMLElement | null;
    
    // 设置 will-change 提示浏览器进行 GPU 加速
    if (shiftDropdownElementRef.current) {
      shiftDropdownElementRef.current.style.willChange = 'top, left';
    }
    
    const updatePosition = () => {
      const inputElement = shiftInputRefs.current[dateKey];
      if (!inputElement) return;
      
      // 如果下拉菜单引用丢失，尝试重新查找（可能因为 DOM 更新）
      if (!shiftDropdownElementRef.current) {
        shiftDropdownElementRef.current = document.querySelector(`[data-dropdown-id="${dropdownId}"]`) as HTMLElement | null;
        if (shiftDropdownElementRef.current) {
          shiftDropdownElementRef.current.style.willChange = 'top, left';
        }
      }
      
      const rect = inputElement.getBoundingClientRect();
      const newTop = rect.bottom + 4;
      const newLeft = rect.left;
      
      // 如果位置没有变化，跳过更新（减少 DOM 操作）
      if (lastPositionRef.current && 
          Math.abs(lastPositionRef.current.top - newTop) < 0.5 && 
          Math.abs(lastPositionRef.current.left - newLeft) < 0.5) {
        return;
      }
      
      lastPositionRef.current = { top: newTop, left: newLeft };
      
      // 直接更新 DOM，绕过 React 渲染（不调用 setState）
      if (shiftDropdownElementRef.current) {
        shiftDropdownElementRef.current.style.top = `${newTop}px`;
        shiftDropdownElementRef.current.style.left = `${newLeft}px`;
      }
    };
    
    // 同步更新，不使用 requestAnimationFrame，实现真正的 0 延迟
    container.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('scroll', updatePosition, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', updatePosition);
      window.removeEventListener('scroll', updatePosition);
      shiftDropdownElementRef.current = null;
    };
  }, [shiftDropdownVisible, shiftDropdownPosition, calendarScrollContainerRef]);

  // 点击外部关闭下拉菜单
  useEffect(() => {
    if (!shiftDropdownVisible) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isShiftInput =
        target.classList.contains('calendar-day-shift') ||
        (target.tagName === 'INPUT' && target.classList.contains('calendar-day-shift'));
      const isDropdownMenu = target.closest('.dropdown-menu');
      if (!isShiftInput && !isDropdownMenu) {
        setShiftDropdownVisible(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [shiftDropdownVisible]);

  return {
    shiftDropdownVisible,
    setShiftDropdownVisible,
    shiftDropdownPosition,
    shiftDropdownWidth,
    shiftInputRefs,
    shiftEditingStates,
    handleShiftInputClick,
    handleShiftInputFocus,
    handleShiftInputChange,
    handleShiftInputBlur,
    handleShiftSelect,
    shiftMenuItems,
    formatDate,
    getCalDateByDate,
  };
};
